/**
 * FirstSearchDialog — "Sua primeira busca" (proposta "Onboarding"): 3 buscas pensadas para o
 * perfil que a pessoa acabou de responder. "Fazer esta busca" pré-preenche o formulário de
 * captura do módulo (captureFormMemory) e abre a janela de captura; "Prefiro montar minha
 * busca" abre a escolha de módulos.
 *
 * O lugar vem do IP (place.ts). Nos negócios locais, a cidade vira uma tag do campo de locais
 * (pelo mesmo typeahead do formulário; se ele falhar, usa o ponto do IP).
 *
 * Movimento (23/10/2026): o OnboardingHost mantém a janela montada enquanto ela sai (usePresenca)
 * e diz quando pôr .is-aberto. Vindo do perfil, o véu já está na tela e só a janela entra
 * (.ds-dialogo, 200 ms). As 3 buscas entram em cascata (.ds-entra .ds-escalonado, 30 ms entre
 * elas) só quando a janela começa a abrir, 140 ms depois dela (o pai antes dos filhos) e, quando a
 * última chega, "Fazer esta busca" solta o halo amarelo uma vez (.ds-pulso-acao, decisão M12 b).
 * Enquanto a janela sai ela continua montada e com o foco: cada escolha vale uma vez só.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useCaptureStore } from '../../stores/captureStore';
import { emitActivity } from '../../lib/activity';
import { cityLabel, userPlace } from './place';
import { useOnboardingText, type OnboardingKey } from './onboardingText';
import type { OnboardingResult } from './OnboardingDialog';
import { buildSuggestions, fillCaptureForm, placeWords, profileSummary } from './primeiraBusca';
import './onboarding.css';

interface FirstSearchDialogProps {
  profile: OnboardingResult;
  onClose: () => void;
  /** Movimento: janela aberta (.is-aberto); quem monta liga depois de montar e desliga ao sair. */
  aberto?: boolean;
  /** Véu aberto. Vindo do perfil, já nasce aberto (sem transição): continua o véu do perfil. */
  veuAberto?: boolean;
}

const KIND_ICON = { loc: 'storefront', tel: 'call', mail: 'mail' } as const;
const KIND_LABEL: Record<'loc' | 'tel' | 'mail', OnboardingKey> = {
  loc: 'k_loc',
  tel: 'k_tel',
  mail: 'k_mail',
};

export function FirstSearchDialog({
  profile,
  onClose,
  aberto = true,
  veuAberto = true,
}: FirstSearchDialogProps): JSX.Element {
  const { tx, suggestion, lang } = useOnboardingText();
  const openCaptureWindow = useCaptureStore((s) => s.openCaptureWindow);
  const openCategories = useCaptureStore((s) => s.openCategories);
  const place = useMemo(() => userPlace(), []);
  const [picked, setPicked] = useState(0);
  const [busy, setBusy] = useState(false);
  // Halo de "Fazer esta busca": uma vez, quando a última busca termina de entrar.
  const [halo, setHalo] = useState(false);
  // A cascata só começa com a janela abrindo (montada fechada, ela ainda está invisível).
  const [entrou, setEntrou] = useState(aberto);
  if (aberto && !entrou) setEntrou(true);
  // Já escolheu (esta busca ou "Prefiro montar"): a janela está saindo.
  const escolheu = useRef(false);

  const words = useMemo(() => placeWords(tx, lang), [tx, lang]);
  const items = buildSuggestions(profile, place, words, suggestion);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const city = cityLabel(place);
  const hasLocal = items.some((i) => i.def.kind === 'loc');
  const note = !place.city
    ? tx('note_none')
    : profile.reach === 'cidade' || profile.reach === 'regiao'
      ? tx('note_city', { city })
      : profile.reach === 'estado' && (place.state || place.region)
        ? tx('note_state', { state: place.state?.name ?? place.region ?? '', city })
        : hasLocal
          ? tx('note_country', { city })
          : '';

  const profileText = profileSummary(profile, place, words, tx);

  async function apply(): Promise<void> {
    const item = items[picked];
    if (!item || busy || escolheu.current) return;
    escolheu.current = true;
    setBusy(true);
    emitActivity('first_search.chosen', { own: false }, { suggestion: item.id });
    await fillCaptureForm(item.form, place, lang);
    onClose();
    openCaptureWindow(item.form.module);
  }

  function ownSearch(): void {
    if (escolheu.current) return;
    escolheu.current = true;
    emitActivity('first_search.chosen', { own: true });
    onClose();
    openCategories();
  }

  return createPortal(
    <div
      className={`ob-overlay ob-overlay--search ds-veu${veuAberto ? ' is-aberto' : ''}`}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`ob-dialog ob-dialog--search ds-dialogo${aberto ? ' is-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fs-title"
      >
        <div className="fs-head">
          <span className="fs-head__icon material-symbols-rounded" aria-hidden="true">
            auto_awesome
          </span>
          <div>
            <h2 id="fs-title">{tx('fs_title')}</h2>
            <p>{tx('fs_text', { profile: profileText })}</p>
          </div>
          <button
            type="button"
            className="fs-close ds-resposta"
            aria-label={tx('close')}
            onClick={onClose}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              close
            </span>
          </button>
        </div>

        <div className="fs-list" role="radiogroup" aria-labelledby="fs-title">
          {items.map((item, index) => (
            <label
              key={item.id}
              className={`fs-item ds-escolhivel${entrou ? ' ds-entra ds-escalonado' : ''}${picked === index ? ' is-escolhido' : ''}`}
              style={{ '--i': index } as CSSProperties}
              onAnimationEnd={(e) => {
                if (e.target === e.currentTarget && index === items.length - 1) setHalo(true);
              }}
            >
              <input
                type="radio"
                name="first-search"
                checked={picked === index}
                onChange={() => setPicked(index)}
              />
              <span className="fs-item__body">
                <span className="fs-item__tags">
                  <span className={`fs-kind fs-kind--${item.def.kind}`}>
                    <span className="material-symbols-rounded" aria-hidden="true">
                      {KIND_ICON[item.def.kind]}
                    </span>
                    {tx(KIND_LABEL[item.def.kind])}
                  </span>
                  {index === 0 ? (
                    <span className="fs-rec">
                      <span className="material-symbols-rounded" aria-hidden="true">
                        star
                      </span>
                      {tx('recommended')}
                    </span>
                  ) : null}
                </span>
                <span className="fs-item__title">
                  <strong>{item.term}</strong> {item.where}
                </span>
                <span className="fs-item__why">{item.why}</span>
              </span>
            </label>
          ))}
        </div>

        {note ? (
          <p className="fs-note">
            <span className="material-symbols-rounded" aria-hidden="true">
              location_on
            </span>
            {note}
          </p>
        ) : null}

        <div className="fs-foot">
          <button type="button" className="ob-link ds-resposta" onClick={ownSearch}>
            {tx('own_search')}
          </button>
          <button
            type="button"
            className={`ob-primary ds-resposta${halo ? ' ds-pulso-acao' : ''}`}
            disabled={busy}
            onClick={() => void apply()}
          >
            {tx('do_search')}
            <span className="material-symbols-rounded" aria-hidden="true">
              arrow_forward
            </span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
